import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Users, Gift, Zap, Twitter, Trophy, Globe } from "lucide-react";

export default function Referrals() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);
  const [isPublic, setIsPublic] = useState(false);

  useEffect(() => {
    api.get("/referrals/me").then((r) => setData(r.data)).finally(() => setLoading(false));
    api.get("/referrals/leaderboard").then((r) => setLeaderboard(r.data.leaderboard || [])).catch(() => {});
    api.get("/auth/me").then((r) => setIsPublic(!!r.data.creator_public)).catch(() => {});
  }, []);

  const togglePublic = async () => {
    const next = !isPublic;
    try {
      await api.post("/creator/toggle", { is_public: next });
      setIsPublic(next);
      toast.success(next ? "Public profile enabled." : "Public profile disabled.");
    } catch (e) { toast.error("Failed"); }
  };

  const link = data ? `${window.location.origin}/auth?ref=${data.code}` : "";
  const shareText = "I've been using Viral/Co to generate scroll-stopping hooks in seconds — grab 30 days of Pro free on me:";
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(link)}`;

  const copyLink = async () => {
    if (!link) return;
    await navigator.clipboard.writeText(link);
    toast.success("Link copied. Now go share it.");
  };

  const copyCode = async () => {
    if (!data?.code) return;
    await navigator.clipboard.writeText(data.code);
    toast.success("Code copied.");
  };

  if (loading) return <div className="p-10 font-mono text-xs text-zinc-500">// loading referral data...</div>;
  if (!data) return null;

  return (
    <div className="p-6 md:p-10 lg:p-12 space-y-10" data-testid="referrals-page">
      <header className="border-b border-white/10 pb-6">
        <div className="label-eyebrow">// module 05</div>
        <h1 className="heading-display text-4xl sm:text-5xl mt-2">GIVE <span className="text-signal-yellow">30</span>. GET <span className="text-signal-yellow">30</span>.</h1>
        <p className="font-mono text-xs text-zinc-500 mt-3 max-w-2xl">
          // share your link. when a friend upgrades to Pro, both of you get 30 days of Pro — added on top of whatever you already have. no cap.
        </p>
      </header>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
        {[
          { label: "Invited", value: data.invited_count, testid: "ref-stat-invited", icon: Users },
          { label: "Converted", value: data.converted_count, testid: "ref-stat-converted", icon: Zap },
          { label: "Bonus days earned", value: data.bonus_days_earned, testid: "ref-stat-days" },
          { label: "Active bonus", value: data.bonus_expires_at ? new Date(data.bonus_expires_at).toLocaleDateString() : "—", testid: "ref-stat-expires" },
        ].map((s) => (
          <div key={s.label} className="bg-[#0A0A0A] p-6" data-testid={s.testid}>
            <div className="label-eyebrow">{s.label}</div>
            <div className="font-mono text-4xl font-bold mt-3">{s.value}</div>
          </div>
        ))}
      </div>

      {/* Share block */}
      {/* Share block */}
      <div className="brutal-border bg-[#141414] p-5 space-y-3" data-testid="creator-toggle-block">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Globe size={18} className="text-signal-yellow" />
            <div>
              <div className="label-eyebrow">// public creator profile</div>
              <p className="font-mono text-[11px] text-zinc-400 mt-1">
                {isPublic ? (
                  <>Live at <a href={`/creator/${data.code}`} target="_blank" rel="noopener noreferrer" className="text-signal-yellow hover:underline">/creator/{data.code}</a></>
                ) : "Off. Turn on to get a shareable public page showing your top hooks."}
              </p>
            </div>
          </div>
          <button
            data-testid="creator-toggle"
            onClick={togglePublic}
            className={`font-mono text-[11px] uppercase tracking-[0.18em] px-4 py-2 transition-colors border ${
              isPublic ? "bg-signal-yellow text-black border-signal-yellow" : "text-zinc-300 border-white/20 hover:border-signal-yellow hover:text-signal-yellow"
            }`}
          >
            {isPublic ? "Public · ON" : "Turn ON"}
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="brutal-border bg-[#141414] p-6 space-y-4">
          <div className="label-eyebrow">// your unique link</div>
          <div className="bg-black border border-white/10 p-4 font-mono text-xs text-signal-yellow break-all" data-testid="ref-link">
            {link}
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              data-testid="ref-copy-link"
              onClick={copyLink}
              className="bg-signal-yellow text-black font-bold uppercase tracking-[0.18em] text-xs px-5 py-3 hover:bg-signal-yellow-hover transition-colors flex items-center gap-2"
            >
              <Copy size={12} /> Copy link
            </button>
            <a
              href={twitterUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ref-tweet"
              className="border border-white/15 hover:border-signal-yellow hover:text-signal-yellow text-zinc-300 font-mono text-xs uppercase tracking-[0.18em] px-5 py-3 transition-colors flex items-center gap-2"
            >
              <Twitter size={12} /> Post to X
            </a>
          </div>
        </div>

        <div className="brutal-border bg-[#141414] p-6 space-y-4">
          <div className="label-eyebrow">// or share the code</div>
          <button
            data-testid="ref-code"
            onClick={copyCode}
            className="w-full bg-black border-2 border-signal-yellow p-6 font-mono text-3xl md:text-4xl font-bold text-signal-yellow tracking-[0.15em] uppercase text-center hover:bg-signal-yellow/5 transition-colors"
          >
            {data.code}
          </button>
          <p className="font-mono text-[11px] text-zinc-500 leading-relaxed">
            // friends enter this code on the sign-up screen. when they upgrade, we credit both of you automatically.
          </p>
        </div>
      </div>

      {/* How it works */}
      <div>
        <div className="label-eyebrow mb-4">// how it works</div>
        <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {[
            { n: "01", t: "SHARE", d: "Send your link or code to friends, followers, or your Slack." },
            { n: "02", t: "THEY UPGRADE", d: "When they turn on Pro at $19/mo, both accounts light up." },
            { n: "03", t: "COLLECT DAYS", d: `Both get ${data.bonus_days_per_conversion} days of Pro added instantly. Unlimited stacking.` },
          ].map((f) => (
            <div key={f.n} className="bg-[#0A0A0A] p-6 space-y-3">
              <div className="font-mono text-xs text-signal-yellow tracking-[0.25em]">{f.n}</div>
              <h3 className="heading-display text-xl flex items-center gap-2">
                <Gift size={16} className="text-signal-yellow" /> {f.t}
              </h3>
              <p className="font-mono text-xs text-zinc-500 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Leaderboard */}
      <div>
        <div className="label-eyebrow mb-4 flex items-center gap-2">
          <Trophy size={12} className="text-signal-yellow" /> top referrers
        </div>
        {leaderboard.length === 0 ? (
          <div className="brutal-border p-8 bg-[#141414] text-center" data-testid="leaderboard-empty">
            <div className="font-mono text-xs text-zinc-500">// board is empty. be the first name on the wall.</div>
          </div>
        ) : (
          <div className="brutal-border bg-[#141414] divide-y divide-white/10" data-testid="leaderboard">
            {leaderboard.map((row) => (
              <div key={row.rank} data-testid={`leaderboard-row-${row.rank}`} className="flex items-center justify-between p-4 gap-4">
                <div className="flex items-center gap-4 min-w-0">
                  <span className={`font-mono text-xs w-8 ${row.rank <= 3 ? "text-signal-yellow" : "text-zinc-500"}`}>#{String(row.rank).padStart(2, "0")}</span>
                  <span className="font-display font-bold text-sm truncate">{row.name}</span>
                  {row.rank === 1 && <span className="font-mono text-[9px] bg-signal-yellow text-black px-2 py-0.5 uppercase tracking-wider">GOAT</span>}
                </div>
                <div className="flex items-center gap-6 font-mono text-xs text-zinc-400">
                  <span><span className="text-white font-bold">{row.conversions}</span> converted</span>
                  <span className="text-signal-yellow"><span className="font-bold">+{row.bonus_days}d</span></span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
